import { useLocation } from 'react-router-dom';
import { Product } from './Product';
import { Voucher } from './Voucher';
import { parse as parseQueryString } from 'query-string';
import formatPrice from './format-price';

const productMatchesSearchTerm = (product: Product, searchTerm: string): boolean => (
  product.title.toLowerCase().includes(searchTerm)
  || product.category.toLowerCase().includes(searchTerm)
  || (searchTerm.startsWith('$') && formatPrice(product.price).includes(searchTerm))
  || (searchTerm.startsWith('stars:') && `stars:${product.stars}` === searchTerm)
);

const voucherMatchesSearchTerm = (voucher: Voucher, searchTerm: string): boolean => (
  voucher.code.toLowerCase().includes(searchTerm)
  || formatPrice(voucher.discountValue).includes(searchTerm)
);

export const searchWithinProducts = (products: Product[], searchTerms: string[]): Product[] => {
  let matchingProducts = products;
  for (const searchTerm of searchTerms) {
    matchingProducts = matchingProducts.filter(p => productMatchesSearchTerm(p, searchTerm));
  }
  return matchingProducts;
};

export const searchWithinVouchers = (voucher: Voucher[], searchTerms: string[]): Voucher[] => {
  let matchingVouchers = voucher;
  for (const searchTerm of searchTerms) {
    matchingVouchers = matchingVouchers.filter(v => voucherMatchesSearchTerm(v, searchTerm));
  }
  return matchingVouchers;
};

type SearchInfo = {
  searchTerms: string[],
  searchString: string
}

export const parseSearchTerms = (
  search: string | string[] | null,
): {
  searchTerms: string[], searchString: string
} => {
  if (!search) return {
    searchTerms: [],
    searchString: '',
  };

  if (Array.isArray(search)) {
    // It's possible for multiple ?search= terms to be present in some cases.
    // This resolves those problems by recursively applying this function on the array of terms.
    return {
      searchTerms: search
        .map(parseSearchTerms)
        .map(s => s.searchTerms)
        .flat(),
      searchString: search.join(' '),
    };
  }

  return {
    searchTerms: typeof search === 'string'
      ? search.split(/\s+/).map(s => s.toLowerCase())
      : [],
    searchString: search,
  };
};

export const useSearchTerms = (): SearchInfo => {
  const { search } = useLocation();
  const params = parseQueryString(search);
  return parseSearchTerms(params.search);
};
